import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { createAppTheme } from './theme';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeModeProvider, useThemeMode } from './context/ThemeContext';
import { Layout } from './components/layout';
import AdminRoute from './components/common/AdminRoute';

// Pages - 자주 사용하는 페이지 (즉시 로드)
import { Login, Register, Dashboard, MoldList, MoldRegister, MoldEdit, MoldDetail, MoldHistoryPage, Transaction, TransactionHistory, UserManagement, UserProfile, CustomerManagement, CustomerDashboard, NotificationSettings, LocationManagement, LandingPage, Dashboard2 } from './pages';

// Pages - Lazy Loading (직접 경로 import)
const QRScan = React.lazy(() => import('./pages/QRScan'));
const QRTest = React.lazy(() => import('./pages/QRTest'));
const Reports = React.lazy(() => import('./pages/Reports'));
const ProductionReport = React.lazy(() => import('./pages/ProductionReport'));
const Viewer3DFullscreen = React.lazy(() => import('./pages/Viewer3DFullscreen'));
const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const TestDataSetup = React.lazy(() => import('./pages/TestDataSetup'));

// Project Components (V3 new)
import { ProjectList, ProjectForm, ProjectDetailPage } from './components/projects';
import FileManagerPage from './components/fileManager/FileManagerPage';

// Admin Pages
import { StorageSettings } from './pages/admin';

// Loading Fallback Component
const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
    <CircularProgress />
  </Box>
);

// Protected Route Component with role-based access control
interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[]; // 허용된 role 목록 (없으면 모든 role 허용)
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>; // 추후 스피너 컴포넌트로 교체
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // role 체크: allowedRoles가 있고, 현재 user의 role이 포함되지 않으면 대시보드로 리다이렉트
  if (allowedRoles && user.role && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }
  
  return <>{children}</>;
};

// Public Route Component (로그인 시 대시보드로 리다이렉트)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return !user ? <>{children}</> : <Navigate to="/dashboard" />;
};

// AppContent: 테마를 적용하는 컴포넌트
function AppContent() {
  const { mode } = useThemeMode();
  const theme = createAppTheme(mode);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
            {/* Public Routes */}
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            
            <Route path="/register" element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } />
            
            {/* Protected Routes - Dashboard */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Project Routes (V3 new) */}
            <Route path="/projects" element={
              <ProtectedRoute>
                <Layout>
                  <ProjectList />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/projects/new" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <Layout>
                  <ProjectForm />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/projects/:projectId" element={
              <ProtectedRoute>
                <Layout>
                  <ProjectDetailPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/projects/:projectId/edit" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <Layout>
                  <ProjectForm />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/projects/:projectId/files" element={
              <ProtectedRoute>
                <Layout>
                  <FileManagerPage parentType="project" />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Mold Routes */}
            <Route path="/molds" element={
              <ProtectedRoute>
                <Layout>
                  <MoldList />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/molds/new" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <Layout>
                  <MoldRegister />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/molds/history" element={
              <ProtectedRoute>
                <Layout>
                  <MoldHistoryPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/molds/:id" element={
              <ProtectedRoute>
                <Layout>
                  <MoldDetail />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/molds/:id/edit" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <Layout>
                  <MoldEdit />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/molds/:id/files" element={
              <ProtectedRoute>
                <Layout>
                  <FileManagerPage parentType="mold" />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/molds/:id/history" element={
              <ProtectedRoute>
                <Layout>
                  <MoldHistoryPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* QR Routes */}
            <Route path="/qr/scan" element={
              <ProtectedRoute>
                <Layout>
                  <QRScan />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/qr/test" element={
              <ProtectedRoute>
                <Layout>
                  <QRTest />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Transaction Routes */}
            <Route path="/transactions" element={
              <ProtectedRoute>
                <Layout>
                  <TransactionHistory />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/transactions/new" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <Layout>
                  <Transaction />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* User Routes */}
            <Route path="/users" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout>
                  <UserProfile />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Customer Routes */}
            <Route path="/customers" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <Layout>
                  <CustomerManagement />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/customer-dashboard" element={
              <ProtectedRoute allowedRoles={['customer']}>
                <Layout>
                  <CustomerDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Report Routes */}
            <Route path="/reports" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <Layout>
                  <Reports />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/production-report" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <Layout>
                  <ProductionReport />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Settings Routes */}
            <Route path="/notification-settings" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <NotificationSettings />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/storage" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <StorageSettings />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Location Routes */}
            <Route path="/locations" element={
              <ProtectedRoute allowedRoles={['admin', 'user']}>
                <Layout>
                  <LocationManagement />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Chat Routes */}
            <Route path="/chat" element={
              <ProtectedRoute>
                <Layout>
                  <ChatPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Landing & Additional Pages */}
            <Route path="/landing" element={<LandingPage />} />
            
            <Route path="/dashboard2" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard2 />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/test-setup" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <TestDataSetup />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/3d-viewer/:moldId" element={
              <ProtectedRoute>
                <Viewer3DFullscreen />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes - TODO: V2에서 복사 필요 */}
            
            {/* Default route */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

// App: ThemeModeProvider로 감싸는 최상위 컴포넌트
function App() {
  return (
    <ThemeModeProvider>
      <AppContent />
    </ThemeModeProvider>
  );
}

export default App;
